import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, FormControlLabel, Switch, useTheme, Stack, Button, Select, MenuItem, FormControl, InputLabel, Card, CardContent, Chip, Grid, CircularProgress, Tooltip, Snackbar, Alert
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { CollectionPoint, collectionPointsService } from '../../services/collectionpoints-service';
import { Department, departmentService } from '../../services/department-service';
import { DataElement, dataElementsService } from '../../services/dataelements-service';

const COLLECTION_POINT_TYPES = [
    { value: 'API', label: 'API', description: 'Privigo provides API endpoints for consent management. Clients create and host their own forms, then submit consent records through the API.' },
    { value: 'HOSTED_WEBFORM', label: 'Hosted Webform', description: 'Use Privigo form designer to create forms hosted on our platform. Forms can be displayed on client websites using iframe integration.' },
    { value: 'EMBEDDED_WEBFORM', label: 'Embedded Webform', description: 'Forms created with Privigo designer are embedded directly into client websites using JavaScript integration.' },
    { value: 'NOTICE', label: 'Notice', description: 'For existing data principals with client relationships. Create notices to request additional information or consent, accessible through the DP portal.' },
    { value: 'BULK_UPLOAD', label: 'Bulk Upload', description: 'Upload existing PII data collected offline (e.g., paper forms) via Excel sheets. Data principals receive email notices to provide consent.' }
];

interface DetailsTabProps {
    collectionPoint: CollectionPoint | null;
    isPublished: boolean;
    onDetailsChanged?: () => void;
    onDetailsChangedUnsaved?: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ collectionPoint, isPublished, onDetailsChanged, onDetailsChangedUnsaved }) => {
    const theme = useTheme();
    const [collectionPointData, setCollectionPointData] = useState<CollectionPoint | null>(null);
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [identifierDataElements, setIdentifierDataElements] = useState<DataElement[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [departmentId, setDepartmentId] = useState<number | ''>('');
    const [collectionPointType, setCollectionPointType] = useState('0');
    const [identifierDataElementId, setIdentifierDataElementId] = useState<number | ''>('');
    const [isActive, setIsActive] = useState(false);
    const [IsPublished, setIsPublished] = useState(false);
    const [enableMinorSupport, setEnableMinorSupport] = useState(false);
    const [parentIdentifierDataElementId, setParentIdentifierDataElementId] = useState<number | ''>('');
    const [enableDoubleOptIn, setEnableDoubleOptIn] = useState(false);
    const [trackGeolocation, setTrackGeolocation] = useState(false);
    const [enableCollectionPointEvents, setEnableCollectionPointEvents] = useState(false);
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Load collection point data on mount or when ID changes
    useEffect(() => {
        if (collectionPoint?.CollectionPointId) {
            setLoading(true);
            collectionPointsService.getCollectionPointById(collectionPoint.CollectionPointId)
                .then(data => {
                    setCollectionPointData(data);
                    setName(data.Name || '');
                    setDescription(data.Description || '');
                    setDepartmentId(data.DepartmentId || '');
                    setCollectionPointType(data.CollectionPointType || '0');
                    setIdentifierDataElementId(data.IdentifierDataElementId || '');
                    setIsActive(!!data.IsActive);
                    setIsPublished(!!data.IsPublished);
                    setEnableMinorSupport(!!data.EnableParentChildRelationship);
                    setParentIdentifierDataElementId(data.ParentIdentifierDataElementId || '');
                    setEnableDoubleOptIn(!!data.EnableDoubleOptIn);
                    setTrackGeolocation(!!data.TrackGeolocation);
                    setEnableCollectionPointEvents(!!data.EnableCollectionPointEvents);
                })
                .catch(() => setError('Failed to load details'))
                .finally(() => setLoading(false));
        }
    }, [collectionPoint?.CollectionPointId]);

    // Load departments and identifier data elements
    useEffect(() => {
        departmentService.getDepartments().then(depts => {
            console.log('Loaded departments:', depts);
            setDepartments(depts);
        }).catch(err => {
            console.error('Error loading departments:', err);
        });
        dataElementsService.getDataElements({ isIdentifier: true, isPublished: true })
            .then(res => setIdentifierDataElements(res.elements));
    }, []);

    // Debug: Log department state changes
    useEffect(() => {
        console.log('Department state - departmentId:', departmentId, 'departments count:', departments.length);
    }, [departmentId, departments]);

    const handleSaveDetails = async () => {
        if (!collectionPointData) return;
        // Add validation as needed
        setIsSavingDetails(true);
        setError(null);
        try {
            const payload = {
                Name: name,
                Description: description,
                DepartmentId: departmentId !== '' ? Number(departmentId) : undefined,
                IdentifierDataElementId: identifierDataElementId !== '' ? Number(identifierDataElementId) : undefined,
                TrackGeolocation: trackGeolocation,
                EnableCollectionPointEvents: enableCollectionPointEvents,
                EnableParentChildRelationship: enableMinorSupport,
                ParentIdentifierDataElementId: parentIdentifierDataElementId !== '' ? Number(parentIdentifierDataElementId) : undefined,
                EnableDoubleOptIn: enableDoubleOptIn,
            };
            const updated = await collectionPointsService.updateCollectionPoint(collectionPointData.CollectionPointId, payload);
            setCollectionPointData(updated);
            setName(updated.Name || '');
            setDescription(updated.Description || '');
            setDepartmentId(updated.DepartmentId || '');
            setCollectionPointType(updated.CollectionPointType || '0');
            setIdentifierDataElementId(updated.IdentifierDataElementId || '');
            setIsActive(!!updated.IsActive);
            setIsPublished(!!updated.IsPublished);
            setEnableMinorSupport(!!updated.EnableParentChildRelationship);
            setParentIdentifierDataElementId(updated.ParentIdentifierDataElementId || '');
            setEnableDoubleOptIn(!!updated.EnableDoubleOptIn);
            setTrackGeolocation(!!updated.TrackGeolocation);
            setEnableCollectionPointEvents(!!updated.EnableCollectionPointEvents);
            setSuccessMessage('Collection point updated successfully!');
            if (onDetailsChanged) onDetailsChanged();
        } catch (e) {
            setError('Failed to save changes.');
        } finally {
            setIsSavingDetails(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <Typography color="text.primary">Loading...</Typography>
            </Box>
        );
    }
    if (!collectionPointData) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="text.primary">No data.</Typography>
            </Box>
        );
    }

    return (
        <>
            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage('')}
                message={successMessage}
            />

            <Box sx={{ p: 3, pr: 6 }}>
                {/* Error Alert */}
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ mb: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading states */}
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography color="text.primary">Loading form data...</Typography>
                    </Box>
                )}

                <Stack spacing={3}>
                    {/* Basic Information Card */}
                    <Card variant="outlined" sx={{ bgcolor: theme.palette.background.paper }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                                Basic Information
                            </Typography>
                            
                            {/* Header with Identifier and Type */}
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2, 
                                mb: 3, 
                                p: 2, 
                                bgcolor: theme.palette.background.default, 
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Identifier:
                                    </Typography>
                                    <Chip
                                        label={collectionPointData?.CollectionPointIdentifier || 'N/A'}
                                        size="small"
                                        sx={{
                                            bgcolor: theme.palette.neutral.main,
                                            color: theme.palette.neutral.contrastText,
                                            '& .MuiChip-label': {
                                                fontWeight: 500
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Type:
                                    </Typography>
                                    <Tooltip 
                                        title={COLLECTION_POINT_TYPES.find(type => type.value === collectionPointData?.CollectionPointType)?.description || 'No description available'}
                                        placement="top"
                                        arrow
                                        sx={{
                                            '& .MuiTooltip-tooltip': {
                                                maxWidth: 400,
                                                fontSize: '0.875rem',
                                                bgcolor: theme.palette.grey[800],
                                                color: theme.palette.common.white,
                                            }
                                        }}
                                    >
                                        <Chip
                                            label={COLLECTION_POINT_TYPES.find(type => type.value === collectionPointData?.CollectionPointType)?.label || 'Unknown'}
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.neutral.main,
                                                color: theme.palette.neutral.contrastText,
                                                '& .MuiChip-label': {
                                                    fontWeight: 500
                                                },
                                                cursor: 'help'
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Stack spacing={3}>
                                {/* Name - full width */}
                                <TextField 
                                    label="Name" 
                                    fullWidth 
                                    size="small" 
                                    required 
                                    value={name} 
                                    onChange={e => {
                                        setName(e.target.value);
                                        onDetailsChangedUnsaved?.();
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: theme.palette.background.paper,
                                            '& fieldset': {
                                                borderColor: theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                        },
                                    }}
                                    disabled={isPublished}
                                />

                                {/* Description - 1 column */}
                                <TextField 
                                    label="Description" 
                                    fullWidth 
                                    size="small" 
                                    multiline 
                                    rows={3} 
                                    value={description} 
                                    onChange={e => {
                                        setDescription(e.target.value);
                                        onDetailsChangedUnsaved?.();
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: theme.palette.background.paper,
                                            '& fieldset': {
                                                borderColor: theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                        },
                                    }}
                                    disabled={isPublished}
                                />

                                {/* Department - full width */}
                                <FormControl fullWidth size="small" required>
                                    <InputLabel id="department-label">Department</InputLabel>
                                    <Select
                                        labelId="department-label"
                                        id="department-select"
                                        value={departmentId || ''}
                                        label="Department"
                                        onChange={e => {
                                            console.log('Department changed to:', e.target.value);
                                            setDepartmentId(e.target.value as number);
                                            onDetailsChangedUnsaved?.();
                                        }}
                                        disabled={isPublished || loading}
                                        native
                                        sx={{
                                            '& .MuiNativeSelect-select': {
                                                padding: '8.5px 14px'
                                            }
                                        }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.departmentId} value={dept.departmentId}>
                                                {dept.departmentName}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Configuration Card */}
                    <Card variant="outlined" sx={{ bgcolor: theme.palette.background.paper }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1.5, color: theme.palette.text.primary, fontWeight: 600 }}>
                                Configuration
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    size="small"
                                                    checked={trackGeolocation} 
                                                    onChange={e => {
                                                        setTrackGeolocation(e.target.checked);
                                                        onDetailsChangedUnsaved?.();
                                                    }}
                                                    disabled={isPublished}
                                                />
                                            }
                                            label="Geolocation"
                                            sx={{ 
                                                '& .MuiFormControlLabel-label': { 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        <Tooltip 
                                            title="Geolocation tracking involves collecting precise location data of data principals. Under the DPDP Act, this is considered sensitive personal data requiring explicit consent. When enabled, the system will collect and store location coordinates during consent collection. This must be clearly disclosed in the privacy notice and requires specific consent from data principals as per Section 5 of the DPDP Act."
                                            placement="top"
                                            arrow
                                            sx={{
                                                '& .MuiTooltip-tooltip': {
                                                    maxWidth: 400,
                                                    fontSize: '0.875rem',
                                                    bgcolor: theme.palette.grey[800],
                                                    color: theme.palette.common.white,
                                                }
                                            }}
                                        >
                                            <InfoIcon 
                                                sx={{ 
                                                    fontSize: 16, 
                                                    color: theme.palette.text.secondary,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }} 
                                            />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    size="small"
                                                    checked={enableCollectionPointEvents} 
                                                    onChange={e => setEnableCollectionPointEvents(e.target.checked)}
                                                    disabled={isPublished}
                                                />
                                            }
                                            label="Webhooks"
                                            sx={{ 
                                                '& .MuiFormControlLabel-label': { 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        <Tooltip 
                                            title="Collection Point Events enable real-time integration through webhooks. When enabled, Privigo will send HTTP POST requests to your configured webhook endpoints for events such as: new consent submissions, consent revocation, purpose-specific consent changes, data deletion requests, and consent expiry notifications. Each event payload includes event type, timestamp, data principal identifier, and relevant consent details. This enables seamless integration with your existing systems for real-time consent management and compliance tracking."
                                            placement="top"
                                            arrow
                                            sx={{
                                                '& .MuiTooltip-tooltip': {
                                                    maxWidth: 400,
                                                    fontSize: '0.875rem',
                                                    bgcolor: theme.palette.grey[800],
                                                    color: theme.palette.common.white,
                                                }
                                            }}
                                        >
                                            <InfoIcon 
                                                sx={{ 
                                                    fontSize: 16, 
                                                    color: theme.palette.text.secondary,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }} 
                                            />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    size="small"
                                                    checked={enableDoubleOptIn} 
                                                    onChange={e => {
                                                        setEnableDoubleOptIn(e.target.checked);
                                                        onDetailsChangedUnsaved?.();
                                                    }}
                                                    disabled={isPublished}
                                                />
                                            }
                                            label="Double Opt-In"
                                            sx={{ 
                                                '& .MuiFormControlLabel-label': { 
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        <Tooltip 
                                            title="Double Opt-In is an ethical consent verification practice where Privigo sends a confirmation email to the data principal after initial consent submission. This two-step verification process ensures that consent is genuinely provided by the data principal and helps prevent unauthorized consent submissions. While this practice enhances consent integrity and builds trust, it is optional and at the discretion of the client organization. The confirmation email includes consent details and provides an opportunity for the data principal to review and confirm their consent decision."
                                            placement="top"
                                            arrow
                                            sx={{
                                                '& .MuiTooltip-tooltip': {
                                                    maxWidth: 400,
                                                    fontSize: '0.875rem',
                                                    bgcolor: theme.palette.grey[800],
                                                    color: theme.palette.common.white,
                                                }
                                            }}
                                        >
                                            <InfoIcon 
                                                sx={{ 
                                                    fontSize: 16, 
                                                    color: theme.palette.text.secondary,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }} 
                                            />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Identifier Data Element Card */}
                    <Card variant="outlined" sx={{ bgcolor: theme.palette.background.paper }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                    Identifier Data Element
                                </Typography>
                                <Tooltip 
                                    title="This field defines the unique identifier used to distinguish individual data principals when they interact with this collection point"
                                    placement="top"
                                    arrow
                                    sx={{
                                        '& .MuiTooltip-tooltip': {
                                            maxWidth: 400,
                                            fontSize: '0.875rem',
                                            bgcolor: theme.palette.grey[800],
                                            color: theme.palette.common.white,
                                        }
                                    }}
                                >
                                    <InfoIcon 
                                        sx={{ 
                                            fontSize: 20, 
                                            color: theme.palette.text.secondary,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.primary.main
                                            }
                                        }} 
                                    />
                                </Tooltip>
                            </Box>
                            <Stack spacing={2}>
                                {/* Identifier Data Element Selection */}
                                <FormControl fullWidth size="small" required>
                                    <InputLabel id="identifier-data-element-label">Identifier Data Element</InputLabel>
                                    <Select
                                        labelId="identifier-data-element-label"
                                        id="identifier-data-element-select"
                                        value={identifierDataElementId || ''}
                                        label="Identifier Data Element"
                                        onChange={e => {
                                            const value = e.target.value as number;
                                            setIdentifierDataElementId(value);
                                            onDetailsChangedUnsaved?.();
                                        }}
                                        disabled={isPublished || loading}
                                        native
                                        sx={{
                                            '& .MuiNativeSelect-select': {
                                                padding: '8.5px 14px'
                                            }
                                        }}
                                    >
                                        <option value="">Select Identifier Data Element</option>
                                        {identifierDataElements.map(element => (
                                            <option key={element.elementId} value={element.elementId}>
                                                {element.elementName}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Minor Support Card */}
                    <Card variant="outlined" sx={{ bgcolor: theme.palette.background.paper }}>
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                                <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                    Minor Support
                                </Typography>
                                <Tooltip 
                                    title="Under the DPDP Act, minors (persons under 18 years) require parental consent for data processing. This feature enables collection points to identify and validate parental consent through a parent identifier data element. When enabled, the system will require a parent identifier (such as parent's email or phone) to be provided alongside the minor's data, ensuring compliance with the Act's provisions for protecting minors' privacy rights."
                                    placement="top"
                                    arrow
                                    sx={{
                                        '& .MuiTooltip-tooltip': {
                                            maxWidth: 400,
                                            fontSize: '0.875rem',
                                            bgcolor: theme.palette.grey[800],
                                            color: theme.palette.common.white,
                                        }
                                    }}
                                >
                                    <InfoIcon 
                                        sx={{ 
                                            fontSize: 16, 
                                            color: theme.palette.text.secondary,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.primary.main
                                            }
                                        }} 
                                    />
                                </Tooltip>
                            </Box>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                size="small"
                                                checked={enableMinorSupport} 
                                                onChange={e => setEnableMinorSupport(e.target.checked)}
                                                disabled={isPublished}
                                            />
                                        }
                                        label="Enable Minor Support"
                                        sx={{ 
                                            '& .MuiFormControlLabel-label': { 
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    {enableMinorSupport && (
                                        <FormControl fullWidth size="small" required>
                                            <InputLabel id="parent-identifier-label">Parent Identifier *</InputLabel>
                                            <Select
                                                labelId="parent-identifier-label"
                                                id="parent-identifier-select"
                                                value={parentIdentifierDataElementId || '0'}
                                                label="Parent Identifier *"
                                                onChange={e => setParentIdentifierDataElementId(e.target.value as number)}
                                                disabled={isPublished || loading}
                                                native
                                                sx={{
                                                    '& .MuiNativeSelect-select': {
                                                        padding: '8.5px 14px'
                                                    }
                                                }}
                                            >
                                                <option value="0">Select Parent Identifier</option>
                                                {identifierDataElements
                                                    .filter(element => element.isIdentifier)
                                                    .map(element => (
                                                        <option key={element.elementId} value={element.elementId}>
                                                            {element.elementName}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>


                    {/* System Information Card */}
                    <Card variant="outlined" sx={{ bgcolor: theme.palette.background.paper }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1.5, color: theme.palette.text.primary, fontWeight: 600 }}>
                                System Information
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                                            Created:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.875rem' }}>
                                            {collectionPointData?.CreatedDate ? 
                                                new Date(collectionPointData.CreatedDate).toLocaleDateString('en-US', { 
                                                    year: 'numeric', 
                                                    month: 'short', 
                                                    day: 'numeric',
                                                    timeZone: 'UTC'
                                                }) 
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                                            Modified:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.875rem' }}>
                                            {collectionPointData?.ModifiedDate ? 
                                                new Date(collectionPointData.ModifiedDate).toLocaleDateString('en-US', { 
                                                    year: 'numeric', 
                                                    month: 'short', 
                                                    day: 'numeric',
                                                    timeZone: 'UTC'
                                                }) 
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Save Button */}
                    {!isPublished && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={isSavingDetails || loading}
                                onClick={handleSaveDetails}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: theme.palette.action.disabledBackground,
                                        color: theme.palette.action.disabled,
                                    },
                                    minWidth: 100,
                                }}
                            >
                                {isSavingDetails ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </Box>
        </>
    );
};

export default DetailsTab; 